import { takeLatest, call, put, all, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';

// Services
import { operatorsApi } from 'services/apiDigitalTicket/operators';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from 'services/firebase';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Constants
import ENVS from 'constants/enviroments';
import { errorMessagersList } from 'helpers/errorMessagers';
import PATH from 'constants/basePath';

// Helpers
import { isObject } from 'helpers/isObject';
import { getClaims } from 'helpers/auth';

import * as TransportOperatorsActions from 'store/ducks/transportOperators/actions';
import { TransportOperatorInterface } from 'store/ducks/transportOperators/types';

import * as OperatorsActions from './actions';
import { OperatorsTypes } from './types';

function* list({
  payload,
}: ReturnType<typeof OperatorsActions.listOperatorRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando empresa' }));

    const response = yield call(operatorsApi.get, {
      params: { ...payload },
    });

    if (response.status === 200) {
      if (isObject(response.data)) {
        yield put(
          OperatorsActions.listOperatorSuccess({
            operatorsList: response.data,
          }),
        );
      }

      if (Array.isArray(response.data)) {
        yield put(
          OperatorsActions.listOperatorSuccess({
            operatorsList: response.data,
          }),
        );
      }

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('operators - listOperators - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(OperatorsActions.listOperatorFailure());
    yield put(DialogsActions.setClose());
  }
}

function* create({
  payload,
}: ReturnType<typeof OperatorsActions.createOperatorRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando empresa' }));
    const response = yield call(operatorsApi.create, { data: payload });
    if (response.status === 200) {
      // Logged User Email
      const claims = getClaims();
      let loggedUserEmail: string;
      try {
        const parsedClaims = JSON.parse(claims!);
        loggedUserEmail = parsedClaims.loggedUserEmail;
      } catch (e) {
        // Do nothing, there is no logged user
      }
      // Send password email
      yield all(
        payload.supervisors.map((supervisor) =>
          call(
            () =>
              supervisor.email !== loggedUserEmail &&
              sendPasswordResetEmail(auth, supervisor.email, {
                url:
                  ENVS.PASSWORD_REDIRECT || 'https://bilhetagemdigital.com.br',
              }),
          ),
        ),
      );

      yield put(
        OperatorsActions.createOperatorSuccess({
          operatorCreated: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Empresa criada com sucesso',
        }),
      );

      if (window.location.pathname === PATH.SELECT_WORKSPACE.CREATE) {
        yield put(push(window.location.pathname.replace('/criar', '')));
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('operators - createOperator - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(OperatorsActions.createOperatorFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof OperatorsActions.updateOperatorRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando empresa' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(operatorsApi.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      const operatorsList: TransportOperatorInterface[] = yield select(
        (state) => state.transportOperators.transportOperatorsList || [],
      );

      const updatedSelected: TransportOperatorInterface = response.data;

      const updatedList = operatorsList.map((op) =>
        op.id === updatedSelected.id ? updatedSelected : op,
      );

      yield put(
        TransportOperatorsActions.updateTransportOperatorSuccess({
          transportOperatorsList: updatedList,
          transportOperatorSelected: updatedSelected,
        }),
      );
      yield put(OperatorsActions.updateOperatorSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Empresa alterada com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('operators - update - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(OperatorsActions.updateOperatorFailure());
    yield put(DialogsActions.setClose());
  }
}

function* selectOperator({
  payload,
}: ReturnType<typeof OperatorsActions.selectOperatorRequest>) {
  try {
    yield put(
      OperatorsActions.selectOperatorSuccess({
        operatorSelected: payload.operatorSelected,
      }),
    );

    // eslint-disable-next-line no-undef
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('transportOperators - select - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
  }
}

// eslint-disable-next-line import/prefer-default-export
export const operatorsSagas = [
  takeLatest(OperatorsTypes.LIST_OPERATOR_REQUEST, list),
  takeLatest(OperatorsTypes.CREATE_OPERATOR_REQUEST, create),
  takeLatest(OperatorsTypes.UPDATE_OPERATOR_REQUEST, update),
  takeLatest(OperatorsTypes.SELECT_OPERATOR_REQUEST, selectOperator),
];
